import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import type { Account, AccountRequest, AccountUpdateRequest } from '@/types/account';
import { ProductSelector } from './ProductSelector';
import { ProductDomain } from '../../types/product';
import { DomainInput } from './DomainInput';

interface AccountFormProps {
  mode: 'create' | 'edit';
  initialData?: Account;
  onSubmit: (data: AccountRequest | AccountUpdateRequest) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  productDomains?: ProductDomain[];
}

interface FormData {
  name: string;
  accountToken: string;
  remark: string;
  userFeedbackTableName: string;
  userLastFeedbackTableName: string;
  allowedDomains: string[];
  isActive: boolean;
  productTitle: string | null;     // e.g., "Access Nexus" - maps to Account.ProductName
  productCode: string | null;      // e.g., "9002" - maps to Account.ProductCode (reporting code)
}

interface FormErrors {
  name?: string;
  accountToken?: string;
  userFeedbackTableName?: string;
  userLastFeedbackTableName?: string;
  remark?: string;
  allowedDomains?: string;
  productName?: string;
}

export const AccountForm: React.FC<AccountFormProps> = ({
  mode,
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  productDomains,
}) => {
  const isEditMode = mode === 'edit';

  const [formData, setFormData] = useState<FormData>({
    name: initialData?.name || '',
    accountToken: initialData?.accountToken || '',
    remark: initialData?.remark || '',
    userFeedbackTableName: initialData?.userFeedbackTableName || '',
    userLastFeedbackTableName: initialData?.userLastFeedbackTableName || '',
    allowedDomains: initialData?.allowedDomains || [],
    isActive: initialData?.isActive ?? true,
    productTitle: initialData?.productName ?? null,
    productCode: initialData?.productCode ?? null,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        accountToken: initialData.accountToken || '',
        remark: initialData.remark || '',
        userFeedbackTableName: initialData.userFeedbackTableName || '',
        userLastFeedbackTableName: initialData.userLastFeedbackTableName || '',
        allowedDomains: initialData.allowedDomains || [],
        isActive: initialData.isActive ?? true,
        productTitle: initialData.productName ?? null,
        productCode: initialData.productCode ?? null,
      });
    }
  }, [initialData]);

  const validateField = (name: keyof FormData, value: string | boolean): string | undefined => {
    if (isEditMode) {
      if (name === 'remark' && typeof value === 'string') {
        if (value.length > 100) {
          return 'Remark must be 100 characters or less';
        }
      }
      return undefined;
    }

    if (name === 'name' && !value) {
      return 'Name is required';
    }
    if (name === 'remark' && typeof value === 'string' && value.length > 100) {
      return 'Remark must be 100 characters or less';
    }

    return undefined;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!isEditMode) {
      if (!formData.name) newErrors.name = 'Name is required';
    }

    // Product is required in both create and edit modes
    if (!formData.productCode) newErrors.productName = 'Product is required';

    if (formData.remark && formData.remark.length > 100) {
      newErrors.remark = 'Remark must be 100 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const fieldValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;

    setFormData(prev => ({ ...prev, [name]: fieldValue }));

    if (touched[name]) {
      const error = validateField(name as keyof FormData, fieldValue);
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));

    const error = validateField(name as keyof FormData, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  // ProductSelector passes: (name=slug, code=reportingCode, title=displayName)
  const handleProductChange = (_slug: string | null, reportingCode: string | null, title: string | null) => {
    setFormData(prev => ({
      ...prev,
      productTitle: title,         // Display name -> ProductName in API
      productCode: reportingCode,  // Reporting code -> ProductCode in API
    }));
    // Clear error when product is selected
    if (reportingCode) {
      setErrors(prev => ({ ...prev, productName: undefined }));
    }
    setTouched(prev => ({ ...prev, productName: true }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const allTouched = Object.keys(formData).reduce((acc, key) => ({ ...acc, [key]: true }), {});
    setTouched(allTouched);

    if (!validateForm()) {
      return;
    }

    try {
      if (isEditMode) {
        const updateData: AccountUpdateRequest = {
          isActive: formData.isActive,
          remark: formData.remark || '',
          allowedDomains: formData.allowedDomains,
          productName: formData.productTitle ?? undefined,
          productCode: formData.productCode ?? undefined,  // Reporting code
        };
        await onSubmit(updateData);
      } else {
        const createData: AccountRequest = {
          name: formData.name,
          remark: formData.remark || '',
          allowedDomains: formData.allowedDomains,
          productName: formData.productTitle ?? undefined,
          productCode: formData.productCode ?? undefined,  // Reporting code
        };
        await onSubmit(createData);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const remarkLength = formData.remark?.length || 0;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Name field */}
      {!isEditMode ? (
        <div className="space-y-2">
          <Label htmlFor="name">Name <span className="text-destructive">*</span></Label>
          <Input
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={isLoading}
            placeholder="Enter unique account name"
            className={errors.name && touched.name ? 'border-destructive' : ''}
          />
          {errors.name && touched.name && (
            <p className="text-sm text-destructive">{errors.name}</p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <Label>Name</Label>
          <p className="text-sm py-2 px-3 bg-muted rounded-md">{formData.name}</p>
        </div>
      )}

      {/* Account Token (Edit mode) */}
      {isEditMode && (
        <div className="space-y-2">
          <Label>Account Token</Label>
          <p className="text-xs font-mono py-2 px-3 bg-muted rounded-md truncate">{formData.accountToken}</p>
        </div>
      )}

      {/* Is Active (Edit mode) */}
      {isEditMode && (
        <div className="flex items-center justify-between py-2">
          <Label htmlFor="isActive" className="cursor-pointer">Active</Label>
          <Switch
            id="isActive"
            checked={formData.isActive}
            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, isActive: checked }))}
            disabled={isLoading}
          />
        </div>
      )}

      {/* Remark */}
      <div className="space-y-2">
        <Label htmlFor="remark">Remark</Label>
        <Textarea
          id="remark"
          name="remark"
          value={formData.remark}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={isLoading}
          placeholder="Optional description (max 100 characters)"
          rows={2}
          className={errors.remark && touched.remark ? 'border-destructive' : ''}
        />
        <p className="text-xs text-muted-foreground">{remarkLength}/100 characters</p>
        {errors.remark && touched.remark && (
          <p className="text-sm text-destructive">{errors.remark}</p>
        )}
      </div>

      {/* Product Selector */}
      {productDomains && productDomains.length > 0 && (
        <div className="space-y-2">
          <Label>Product <span className="text-destructive">*</span></Label>
          <ProductSelector
            domains={productDomains}
            selectedProductName={null}
            selectedProductCode={formData.productCode}
            onChange={handleProductChange}
            disabled={isLoading}
          />
          {errors.productName && touched.productName && (
            <p className="text-sm text-destructive">{errors.productName}</p>
          )}
        </div>
      )}

      {/* Table names (Edit mode) */}
      {isEditMode && (
        <>
          <div className="space-y-2">
            <Label>User Feedback Table Name</Label>
            <p className="text-sm font-mono py-2 px-3 bg-muted rounded-md">{formData.userFeedbackTableName}</p>
          </div>
          <div className="space-y-2">
            <Label>User Last Feedback Table Name</Label>
            <p className="text-sm font-mono py-2 px-3 bg-muted rounded-md">{formData.userLastFeedbackTableName}</p>
          </div>
        </>
      )}

      {/* Allowed Domains */}
      <div className="space-y-2">
        <Label>Allowed Domains</Label>
        <DomainInput
          value={formData.allowedDomains}
          onChange={(domains) => setFormData(prev => ({ ...prev, allowedDomains: domains }))}
          disabled={isLoading}
        />
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isLoading}
        >
          {isLoading ? 'Saving...' : (isEditMode ? 'Save Changes' : 'Create')}
        </Button>
      </div>
    </form>
  );
};
